import { useState } from "react";
import { Text } from "react-native";
import { format } from "date-fns";
import { View } from "ui/atoms/view";
import {
  CellListColumnModel,
  CellsList,
} from "app/features/shiftHandover/molecules/CellsList";
import { UrgentConcernModel } from "app/services/api/types/shiftHandover/models/UrgentConcernModel";
import { UrgentConcernStatusEnum } from "app/services/api/types/shiftHandover/enums/UrgentConcernStatusEnum";
import {
  getStatusBackgroundColorClass,
  getStatusTextColorClass,
} from "ui/utils/statusColorMap";
import { NoItemsComponent } from "ui/atoms/noItems";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import AddConcernCommentAction from "app/features/shiftHandover/molecules/AddConcernCommentAction";
import CommentUrgentConcernModal from "app/features/shiftHandover/molecules/CommentUrgentConcernModal";
import UrgentConcernActions from "app/features/shiftHandover/molecules/UrgentConcernsActions";
import { UrgentConcernTypeBadge } from "app/features/shiftHandover/atoms/UrgentConcernTypeBadge";
import {
  useDeleteUrgentConcernMutation,
  useUpdateUrgentConcernStatusMutation,
} from "app/services/api/shiftHandoverApi";
import { urgentConcernsApi } from "app/services/api/urgentConcernsApi";
import { useDispatch } from "react-redux";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { getUrgentConcernReporterName } from "../utils/getUrgentConcernReporterName";

export const formatTimestamp = (value?: string | null) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd-MM-yyyy HH:mm");
  } catch {
    return "-";
  }
};

interface Props {
  data: UrgentConcernModel[];
  status?: UrgentConcernStatusEnum;
  isEditing?: boolean;
  showTypeBadge?: boolean;
  noDataMessage?: React.ReactNode;
  isFetching?: boolean;
}

export function UrgentConcernRecord({
  data,
  status,
  isEditing = true,
  showTypeBadge = false,
  noDataMessage,
  isFetching,
}: Props) {
  const [updateUrgentConcernState, { isLoading: isUpdating }] =
    useUpdateUrgentConcernStatusMutation();
  const [deleteUrgentConcern, { isLoading: isDeleting }] =
    useDeleteUrgentConcernMutation();
  const { showNewToast, showErrorToast } = useDisplayToast();
  const dispatch = useDispatch();
  const [openConcernId, setOpenConcernId] = useState<number | null>(null);
  const openConcern =
    openConcernId !== null
      ? (data.find((c) => c.id === openConcernId) ?? null)
      : null;
  const isResolved = status === UrgentConcernStatusEnum.RESOLVED;

  const columns: CellListColumnModel<UrgentConcernModel>[] = [
    {
      title: "Description",
      key: "description",
      className: "w-[42%]",
      renderData: (concern) => (
        <View className="flex flex-row justify-between items-center">
          <View className="flex-1 min-w-0">
            {showTypeBadge && <UrgentConcernTypeBadge type={concern.type} />}
            <Text className="font-semibold first-letter:uppercase">
              {concern.description}
            </Text>
            {concern.comment ? (
              <View className="my-2">
                <Text className="first-letter:uppercase">
                  {concern.comment}
                </Text>
              </View>
            ) : null}
          </View>
          {isEditing ? (
            <PermissionCheck
              permission={PermissionsEnums.UPDATE_SHIFT_HANDOVER}
            >
              <AddConcernCommentAction
                onPress={() => setOpenConcernId(concern.id)}
              />
            </PermissionCheck>
          ) : null}
        </View>
      ),
    },
    {
      title: isResolved ? "Resolved at" : "Created at",
      key: "timestamp",
      className: "w-[18%]",
      renderData: (concern) => (
        <Text className="text-sm">
          {formatTimestamp(
            isResolved ? concern.resolved_at : concern.created_at,
          )}
        </Text>
      ),
    },
    {
      title: isResolved ? "Resolved by" : "Created by",
      key: "actor",
      className: "w-[18%]",
      renderData: (concern) => (
        <Text className="text-sm">
          {isResolved
            ? (concern.resolved_by?.name ?? "-")
            : getUrgentConcernReporterName(concern)}
        </Text>
      ),
    },
    {
      title: "Status",
      key: "status",
      className: "w-[12%]",
      renderData: (concern) => {
        const textColor = getStatusTextColorClass(concern.status);
        return (
          <Text className={`${textColor} font-semibold first-letter:uppercase`}>
            {concern.status}
          </Text>
        );
      },
    },
    {
      key: "action",
      className: "w-[10%]",
      renderData: (concern) =>
        concern.status !== UrgentConcernStatusEnum.RESOLVED && isEditing ? (
          <PermissionCheck permission={PermissionsEnums.UPDATE_SHIFT_HANDOVER}>
            <UrgentConcernActions
              concern={concern}
              isDeleting={isDeleting}
              isUpdating={isUpdating}
              onDelete={async (id) => {
                try {
                  await deleteUrgentConcern({ urgentConcernId: id }).unwrap();
                  dispatch(
                    urgentConcernsApi.util.invalidateTags(["UrgentConcerns"]),
                  );
                  showNewToast({
                    title: "Urgent concern deleted",
                    message: "The concern has been deleted successfully.",
                    type: "success",
                  });
                } catch (e) {
                  showErrorToast(e);
                }
              }}
              onUpdate={async (id, status) => {
                try {
                  await updateUrgentConcernState({
                    params: { id },
                    body: { status },
                  }).unwrap();
                  dispatch(
                    urgentConcernsApi.util.invalidateTags(["UrgentConcerns"]),
                  );
                  if (status === UrgentConcernStatusEnum.RESOLVED) {
                    showNewToast({
                      title: "Urgent concern resolved",
                      message: "The concern has been resolved successfully.",
                      type: "success",
                    });
                  }
                } catch (e) {
                  showErrorToast(e);
                }
              }}
            />
          </PermissionCheck>
        ) : null,
    },
  ];

  return (
    <>
      <CommentUrgentConcernModal
        concern={openConcern}
        onClose={() => setOpenConcernId(null)}
      />
      <CellsList
        data={data}
        columns={columns}
        isFetching={isFetching}
        cellBackgroundColor={(concern) =>
          getStatusBackgroundColorClass(concern.status)
        }
        noDataMessage={
          noDataMessage ?? (
            <NoItemsComponent
              title="No urgent concerns"
              subtitle="There are no listed urgent concerns"
            />
          )
        }
      />
    </>
  );
}
